import { useEffect, useState } from "react";
import { ArcNetworkEntry } from "types/fleet";
import { gatewayServiceBaseUrl } from "lib/consts/arcGateway";
import { joinUrl } from "lib/utils/connection";
import { authedFetch } from "lib/utils/authedFetch";

const POLL_INTERVAL_MS = 30_000;

export function useArcNetworkStatus(
    proxyHostname: string | null,
    serialNumbers: string[],
): Record<string, ArcNetworkEntry[]> {
    const [statusMap, setStatusMap] = useState<Record<string, ArcNetworkEntry[]>>({});
    const snKey = serialNumbers.slice().sort().join(",");

    useEffect(() => {
        if (!proxyHostname || serialNumbers.length === 0) {
            setStatusMap({});
            return;
        }

        const fetchAll = async () => {
            const results: Record<string, ArcNetworkEntry[]> = {};
            await Promise.allSettled(
                serialNumbers.map(async (sn) => {
                    results[sn] = await fetchActiveNetworks(proxyHostname, sn);
                })
            );
            setStatusMap(results);
        };

        void fetchAll();
        const id = setInterval(() => void fetchAll(), POLL_INTERVAL_MS);
        return () => clearInterval(id);
    // snKey is the stable serialisation of serialNumbers — intentional.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [proxyHostname, snKey]);

    return statusMap;
}

async function fetchActiveNetworks(proxy: string, sn: string): Promise<ArcNetworkEntry[]> {
    const mobileBase = gatewayServiceBaseUrl("mobile", sn, proxy);
    const wifiBase = gatewayServiceBaseUrl("wifi", sn, proxy);
    if (!mobileBase || !wifiBase) return [];

    const mobileUrl = (path: string) => joinUrl(mobileBase, path);
    const wifiUrl = (path: string) => joinUrl(wifiBase, path);

    const [mobileRes, wifiRes] = await Promise.allSettled([
        authedFetch(mobileUrl("/mobile/status")).then((r) => r.json()),
        authedFetch(wifiUrl("/wifi/status")).then((r) => r.json()),
    ]);

    const active: ArcNetworkEntry[] = [];

    // LTE
    if (mobileRes.status === "fulfilled") {
        const m = mobileRes.value;
        const dataUp =
            m?.present !== false &&
            (m?.state === "connected" || m?.bearer_connected === true);
        if (dataUp) {
            active.push({
                type: "LTE",
                signal: typeof m.signal_quality_percent === "number"
                    ? m.signal_quality_percent
                    : undefined,
            });
        }
    }

    // WiFi — fetch cached signal only when connected (no rescan)
    if (wifiRes.status === "fulfilled" && isNmConnected(wifiRes.value?.state)) {
        let signal: number | undefined;
        try {
            const r = await authedFetch(wifiUrl("/wifi/signal"));
            const j = await r.json();
            if (typeof j?.signal === "number") signal = j.signal;
        } catch {}
        active.push({ type: "WiFi", signal });
    }

    return active;
}

// Backend humanizes NM state: "connected", "disconnected", etc.
// Strict equality avoids "disconnected" matching a substring check.
function isNmConnected(state: string | null | undefined): boolean {
    return state === "connected";
}
